import {
  ActionsContainer,
  Bold,
  CellContainer,
  CellStartContainer,
  DeleteButtonIcon,
  EditButtonIcon,
  EditIcon,
  EmptyMessage,
  EmpytContainer,
  IconContainer,
  LockIcon,
  TrashIcon,
  UserIcon,
} from './styles';
import DataTable, { createTheme } from 'react-data-table-component';

import { IDeliveryman } from 'src/types';
import TableSkeleton from 'src/components/TableSkeleton';
import React from 'react';
import { maskToCpf } from 'src/utils/InputMask';
import { useTheme } from 'styled-components';

interface Props {
  deliverymen?: IDeliveryman[];
  isLoading: boolean;
  onEditDeliveryman(deliveryman: IDeliveryman): void;
  onDeleteDeliveryman(deliverymanId: number): void;
}

createTheme('jucaTheme', {
  text: {
    primary: '#FFFFFF',
    secondary: 'rgba(255, 255, 255, 0.7)',
    disabled: 'rgba(0,0,0,.12)',
  },
  background: {
    default: '#424242',
  },
  context: {
    background: '#E91E63',
    text: '#FFFFFF',
  },
  divider: {
    default: 'transparent',
  },
  button: {
    default: '#FFFFFF',
    focus: 'rgba(255, 255, 255, .54)',
    hover: 'rgba(255, 255, 255, .12)',
    disabled: 'rgba(255, 255, 255, .18)',
  },
  selected: {
    default: 'rgba(0, 0, 0, .7)',
    text: '#FFFFFF',
  },
  highlightOnHover: {
    default: 'rgba(0, 0, 0, .7)',
    text: '#FFFFFF',
  },
  striped: {
    default: 'rgba(0, 0, 0, .87)',
    text: '#FFFFFF',
  },
});

const DeliverymenTable: React.FC<Props> = ({
  deliverymen,
  isLoading,
  onEditDeliveryman,
  onDeleteDeliveryman,
}) => {
  const { colors } = useTheme();

  const onEdit = React.useCallback(
    (e: React.MouseEvent, deliveryman: IDeliveryman) => {
      e.preventDefault();
      onEditDeliveryman(deliveryman);
    },
    [onEditDeliveryman],
  );

  const onDelete = React.useCallback(
    (e: React.MouseEvent, deliveryman: IDeliveryman) => {
      e.preventDefault();
      onDeleteDeliveryman(deliveryman.motoboyId);
    },
    [onDeleteDeliveryman],
  );

  const columns = React.useMemo(
    () => [
      {
        name: 'Nome',
        cell: ({ name, enable }: IDeliveryman) => (
          <CellStartContainer>
            <IconContainer>{enable ? <UserIcon /> : <LockIcon />}</IconContainer>
            <Bold enabled={enable}>{name}</Bold>
          </CellStartContainer>
        ),
      },
      {
        name: 'Telefone',
        cell: ({ phone, enable }: IDeliveryman) => (
          <CellContainer enabled={enable}>{phone}</CellContainer>
        ),
      },
      {
        name: 'Email',
        cell: ({ email, enable }: IDeliveryman) => (
          <CellContainer enabled={enable}>{email.toLocaleLowerCase()}</CellContainer>
        ),
      },
      {
        name: 'CPF',
        cell: ({ cpf, enable }: IDeliveryman) => (
          <CellContainer enabled={enable}>
            CPF: {maskToCpf(cpf.toLocaleLowerCase())}
          </CellContainer>
        ),
      },
      {
        name: 'Actions',
        cell: (deliveryman: IDeliveryman) => (
          <ActionsContainer>
            <EditButtonIcon onClick={(event) => onEdit(event, deliveryman)}>
              <EditIcon />
            </EditButtonIcon>
            <DeleteButtonIcon onClick={(event) => onDelete(event, deliveryman)}>
              <TrashIcon color={colors.white} />
            </DeleteButtonIcon>
          </ActionsContainer>
        ),
        width: '100px',
      },
    ],
    [colors.white, onDelete, onEdit],
  );

  const customStyle = React.useMemo(
    () => ({
      table: {
        style: {
          padding: '0 10px 0',
          backgroundColor: colors.mainBackground,
          color: colors.text,
        },
      },
      rows: {
        style: {
          fontSize: '13px',
          color: colors.text,
          backgroundColor: colors.cardBackground,
          boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.15)',
          minHeight: '60px',
          margin: '8px 0 8px',
          borderRadius: '10px',
        },
      },
      pagination: {
        style: {
          backgroundColor: colors.mainBackground,
          color: colors.white,
        },
      },
    }),
    [colors],
  );

  return (
    <DataTable
      subHeader={false}
      noHeader
      noTableHead
      responsive
      pagination
      style={{
        display: 'flex',
        alignItems: deliverymen && deliverymen.length > 0 ? 'flex-start' : 'center',
        justifyContent: 'center',
        height: 'calc(100% - 175px)',
        overflow: 'auto',
      }}
      paginationComponentOptions={{
        rowsPerPageText: 'Quantidade de linhas:',
        rangeSeparatorText: 'Total de Registros:',
        noRowsPerPage: true,
        selectAllRowsItem: true,
        selectAllRowsItemText: 'All',
      }}
      columns={columns}
      data={deliverymen == null ? [] : deliverymen}
      theme="jucaTheme"
      customStyles={customStyle}
      noDataComponent={
        <EmpytContainer>
          {isLoading ? <TableSkeleton /> : <EmptyMessage>Lista vazia</EmptyMessage>}
        </EmpytContainer>
      }
    />
  );
};

export default DeliverymenTable;
